export const profile = {
  communicationPrefs: {
    screenTitle: 'Communication preferences',
    screenSubtitle:
      'Choose what we contact you about. Required notifications are needed for the service to function safely; optional ones are entirely up to you.',
    requiredSectionTitle: 'Required',
    requiredSectionHint: 'Sent over email and/or push. Required to use AssetWRX safely.',
    requiredBadge: 'Required',
    optionalSectionTitle: 'Optional',
    optionalSectionHintLoading: 'Loading your preferences…',
    optionalSectionHint:
      'Toggle each on or off any time. Withdrawing takes effect immediately and is logged in your consent history.',
    alwaysOnBadge: 'Always on',
    alwaysOnHintLegit: "Always on (needed to run the service safely)",
    marketingEmailTitle: 'Product updates, tips & promotions — email',
    marketingEmailBody: 'Occasional emails about new features, workflow patterns, and offers. Off by default.',
    marketingPushTitle: 'Product updates, tips & promotions — push',
    marketingPushBody: 'Push notifications for major launches and offers. Off by default.',
    analyticsTitle: 'Product analytics',
    analyticsBody:
      'Help us understand how AssetWRX is used so we can improve it: screens used, taps, and session recordings with everything you type hidden.',
    crashReportingTitle: 'Crash & stability reporting',
    crashReportingBody: "Crash reports with your personal details removed. Always on, because we need them to keep AssetWRX working.",
    serviceNotifTitle: 'Service notifications',
    serviceNotifBody: 'Incident alerts, billing receipts, and account changes.',
    securityNotifTitle: 'Security notifications',
    securityNotifBody: 'Sign-in alerts, password changes, suspicious-activity warnings.',
    inspectionRemindersTitle: 'Inspection reminders',
    inspectionRemindersBody: 'Reminders for scheduled inspections you own.',
    confirmEmailError: 'Could not send confirmation email. Try again.',
    alreadySubscribed: 'You are already subscribed.',
    confirmationSent: 'Check your email — we sent a confirmation link. Click it within 24 hours to subscribe.',
    updatedFeedback: '{{title}} updated.',
    saveError: 'Could not save your preference. Try again.',
    pendingHint: 'Pending — check your email for the confirmation link (valid 24 h).',
    deliveryPausedHint: 'Subscription paused due to delivery issues. Toggle off and on to retry.',
  },
  consentHistory: {
    loading: 'Loading…',
    errorTitle: 'Error',
    emptyTitle: 'No consent events yet',
    emptyBody: 'Once you accept or change any preference, it will appear here.',
    screenTitle: 'Consent history',
    screenSubtitle: "Every policy you accepted and every marketing or analytics choice you made or withdrew. Entries are kept exactly as they were and cannot be changed. Phone alert settings are not consents and live in Preferences.",
    labelTerms: 'Terms of Service',
    labelPrivacy: 'Privacy Policy',
    labelMarketingEmail: 'Marketing email',
    labelMarketingPush: 'Marketing push',
    labelAnalytics: 'Product analytics',
    labelCrashReporting: 'Crash reporting',
    labelCookieAnalytics: 'Analytics cookies',
    labelCookieMarketing: 'Marketing cookies',
    labelLocationSharing: 'Location sharing while working',
    actionAccepted: 'accepted',
    actionWithdrawn: 'withdrawn',
    actionUpdated: 'updated',
    detailSource: 'Given through',
    detailReason: 'Reason',
    sourcePhonePrompt: 'The app’s analytics question',
    sourceSignup: 'Sign-up',
    sourceCookieBanner: 'Cookie banner',
    sourceSignupCatchup: 'Sign-up (recorded later)',
    // consent_records.device_type
    device: {
      ios: 'iPhone or iPad',
      android: 'Android device',
      web: 'Web browser',
      unknown: 'Unknown device',
    },
  },
  exportData: {
    whatYoullReceiveTitle: 'What you’ll receive',
    itemProfile: 'Your profile and account settings',
    itemAssets: 'All assets, inspections, and jobs you own',
    itemContacts: 'Contacts and team memberships',
    itemRequests: 'Requests you raised and every message on them',
    itemReports: 'Reports and quotes shared with you, with your responses',
    itemConsent: 'Your full consent and audit history',
    itemDeletionLog: 'Account-deletion log entries (if any)',
    itemPriorExports: 'A copy of any prior export requests',
    footnote: "Each download link works for 7 days and only for you.",
    preparing: 'Preparing your export…',
    requestButton: 'Request my data export',
    recentRequestsTitle: 'Recent requests',
    loading: 'Loading…',
    noExportsYet: 'No exports yet — request one above to get started.',
    statusQueued: 'Queued',
    statusProcessing: 'Processing',
    statusReady: 'Ready',
    statusFailed: 'Failed',
    statusExpired: 'Expired',
    requestedAt: 'Requested {{date}}',
    linkExpires: 'Link expires {{date}}',
    linkExpiresSoon: 'Expires within 24 hours · Link expires {{date}}',
    downloadButton: 'Download',
    readyFeedback: 'Your export is ready. Tap “Download” below to save it.',
    screenTitle: 'Export my data',
    screenSubtitle: "You can download a copy of your AssetWRX account data at any time. It comes as a data file that other software can open.",
  },
  privacyDataHub: {
    screenTitle: 'Privacy & data',
    screenSubtitle: "Download your data, choose what we email you, or delete your account. Every change is recorded in your consent history.",
    expandLabel: 'Open',
    collapseLabel: 'Close',
    itemPrivacyTitle: 'Privacy Policy',
    itemPrivacyBody: 'How we collect, use, and protect your data.',
    itemTermsTitle: 'Terms of Service',
    itemTermsBody: 'The agreement that governs your use of AssetWRX.',
    itemCookiesTitle: 'Cookie Policy',
    itemCookiesBody: 'What cookies and similar storage we use, and how to control them.',
    itemSubprocessorsTitle: 'Sub-processors',
    itemSubprocessorsBody: 'The third parties we engage to process data, and where they operate.',
    itemDpaTitle: 'Data Processing Agreement',
    itemDpaBody: "How we handle your content on your behalf.",
    itemMarketingTitle: 'Marketing Consent Policy',
    itemMarketingBody: 'How we obtain, evidence, and respect your opt-in.',
    itemExportTitle: 'Export my data',
    itemExportBody: "Download a copy of everything in your account.",
    itemCommsTitle: 'Communication preferences',
    itemCommsBody: 'Choose which optional updates and tips you receive, and on which channels.',
    itemHistoryTitle: 'Consent history',
    itemHistoryBody: 'Every policy you accepted and every marketing or analytics consent you gave or withdrew.',
    itemAiTitle: 'AI features',
    itemAiBody: 'Whether AssetWRX may send photos and text to Google (Gemini) when you use an AI feature on this device.',
    aiAllowed: 'Allowed on this device. Google processes what an AI feature sends and does not use it to train its models.',
    aiNotAllowed: 'Not allowed. AI features ask before they send anything.',
    aiWithdraw: 'Stop using AI on this device',
    aiAllow: 'Allow AI features',
    itemDeletionPolicyTitle: 'Data Deletion Policy',
    itemDeletionPolicyBody: 'How deletion works, what is retained, and the 30-day recovery window.',
    itemDeleteTitle: 'Delete Account',
    itemDeleteBody: 'Irreversibly delete your account and your data. A 30-day recovery window applies.',
  },
  security: {
    twoFactorSection: "Two-factor sign-in",
    signIn: {
      cancel: "Cancel",
      biometricOffHint: "Off. Turning it off forgets the saved sign-in on this phone.",
      biometricOnHint: "On. {{method}} signs you in on this phone (after one sign-in with your password).",
      biometricTitle: "Sign in with {{method}} on this phone",
      setPasswordFailed: "The link was not sent. Check your connection and try again.",
      setPasswordSent: "We sent a link to {{email}}. Open it to set your password. You can still sign in with Google, Apple or Microsoft.",
      passwordChanged: "Your password is changed. Use it the next time you sign in.",
      passwordFailed: "Your password did not change. Try again.",
      passwordMismatch: "The two passwords are different. Type the same one twice.",
      passwordRequired: "Enter a new password.",
      passwordSave: "Save password",
      passwordConfirmLabel: "Type it again",
      passwordNewLabel: "New password",
      setPassword: "Set a password",
      passwordChange: "Change password",
      passwordNone: "You sign in with Google, Apple or Microsoft, so there is no password yet.",
      passwordSet: "You sign in with a password.",
      passwordTitle: "Password",
      emailSent: "We sent a link to your new address, {{email}}. Open it to finish the change. Until then you keep signing in with {{current}}.",
      emailFailed: "The link was not sent. Check your connection and try again.",
      emailTaken: "Another account already uses that address. Use a different one.",
      emailSame: "That is already your email address.",
      emailInvalid: "Enter an email address like name@company.com.",
      emailSend: "Send link",
      emailNewHelper: "We send a link to the new address. The change happens when you open it.",
      emailNewLabel: "New email address",
      emailPending: "Waiting for you to open the link sent to {{email}}.",
      emailChange: "Change email",
      emailTitle: "Email",
      sectionTitle: "How you sign in",
    },
    title: 'Security',
    removeLastWarning:
      'This is your only authenticator. Two-factor is required, so you will be asked to set one up again immediately.',
    removeBody: 'Remove this authenticator? You can add it again later.',
    removeTitle: 'Remove authenticator',
    cancel: 'Cancel',
    remove: 'Remove',
    removeFailedTitle: 'Could not remove',
    removeFailedBody: 'Please try again.',
    statusOn: 'Two-factor sign-in is on',
    statusOff: 'Two-factor sign-in is off',
    statusSub:
      'Protects your account with a code from an authenticator app (Microsoft Authenticator, Google Authenticator, Authy).',
    methods: 'Your authenticators',
    loading: 'Loading…',
    none: 'No authenticator set up yet.',
    authenticator: 'Authenticator',
    added: 'Added',
    removeA11y: 'Remove authenticator',
    add: 'Add an authenticator',
    addBackup: 'Add a backup authenticator',
  },
  diagnosticsScreen: {
    title: "Diagnostics",
    subtitle: "What support needs to know. Nothing here is secret.",
    sectionBuild: "Build",
    sectionWorkspace: "Workspace",
    sectionConnectivity: "Connectivity",
    sectionServices: "Services",
    appVersion: "App version",
    build: "Build",
    commit: "Git commit",
    runtime: "Runtime version",
    updateChannel: "Update channel",
    platform: "Platform",
    environment: "Environment",
    backend: "Backend",
    workspace: "Workspace",
    role: "Role",
    plan: "Plan",
    capabilities: "Enabled capabilities",
    connectivity: "Connection",
    online: "Online",
    offline: "Offline",
    outbox: "Changes waiting to send",
    lastSync: "Last sent",
    sentry: "Crash reporting",
    sentryOn: "On · release {{release}}",
    sentryOff: "Not configured",
    posthog: "Product analytics",
    posthogOn: "On (consented)",
    posthogOff: "Off (no consent)",
    apiHealth: "API health",
    apiChecking: "Checking…",
    apiOk: "OK · {{latency}} ms · migrations {{head}}",
    apiFail: "Unreachable",
    push: "Push notifications",
    pushRegistered: "Registered with the Expo push service",
    pushNotRegistered: "Not registered on this device (needs a build with push credentials and a granted permission)",
    pushUnsupported: "Not available on this platform",
    copy: "Copy diagnostics",
    copied: "Copied",
    never: "Never",
    unknown: "Unknown",
    personal: "Personal",
    none: "None",
    web: "Web",
  },
  whatsNew: {
    eyebrow: "What's new in AssetWRX",
    gotIt: "Got it",
    seeAll: "See all updates",
    sectionTitle: "What's new",
    empty: "No release notes yet.",
    dateLine: "{{date}} · Version {{version}}",
  },
  aboutScreen: {
    product: "AssetWRX {{version}}",
    title: "About",
    subtitle: "The version of AssetWRX on this device. Copy it when you contact support.",
    build: "Build {{build}}",
    webBuild: "Web",
    runtime: "Runtime {{runtime}}",
    channel: "Channel {{channel}}",
    environment: "Environment {{environment}}",
    update: "Update {{update}}",
    copy: "Copy",
    copied: "Copied",
  },
  helpScreen: {
    contactSupportSubtitle: "Ask a person. Opens our support page in your browser.",
    contactSupportTitle: "Contact AssetWRX support",
    subtitle: 'AssetWRX helps you manage assets, run inspections, and track repair jobs.',
    coreWorkflowTitle: 'Core workflow',
    coreWorkflowBody:
      '• Add assets (vehicles, equipment, property)\n• Start an inspection with a checklist\n• Share the report for customer approval\n• Convert approved repairs to jobs\n• Assign jobs to team members or contractors',
    toursTitle: "Guided tours",
    toursSubtitle: "Step-by-step walkthroughs inside the app: register an asset, run an inspection, set up maintenance.",
    openToursA11y: "Open the guided tours",
    guideTitle: 'User guide',
    guideSubtitle: 'Step-by-step guides for every part of AssetWRX.',
    openGuideA11y: 'Open the User guide',
  },
  pushNotificationPrefs: {
    productTitle: "Product updates",
    productBody: "Replies to your feature requests and news on ideas you voted for.",
    saveFailed: "That setting did not save. Check your connection and try again.",
    permission: {
      onBody: "Choose below which alerts reach you.",
      onTitle: "Notifications are on for this phone",
      openSettings: "Open settings",
      blockedBody: "This phone is not letting AssetWRX send alerts. Turn them on in the phone's Settings, then come back.",
      blockedTitle: "Notifications are off for AssetWRX",
      askAction: "Turn on notifications",
      askBody: "We tell you when work is assigned to you, someone replies, or something waits for your decision. Your phone will ask you to allow it.",
      askTitle: "Get alerts on this phone",
    },
    assignmentTitle: 'Assigned to me',
    assignmentBody: 'Jobs and inspections assigned to you, contractor invites, and awarded work.',
    messageTitle: 'Messages',
    messageBody: 'Replies on jobs, reports shared with you, and your Asset 360 and Site 360 reports when they are ready.',
    approvalTitle: 'Approvals & requests',
    approvalBody:
      'New requests, submitted quotes, completions, and report access asks waiting on your decision.',
    cardTitle: 'Push notifications',
    hintUnsupported:
      'Push notifications are delivered to the AssetWRX mobile app. These settings apply to your phone.',
    hintLoading: 'Loading your settings…',
    hintDefault: 'Choose what reaches your phone. Everything still appears in your in-app inbox either way.',
    saveError: 'Could not save: {{error}}',
    quietHoursTitle: 'Quiet hours',
    quietHoursBody: 'Nothing is pushed during this window. Notifications still arrive in your in-app inbox{{suffix}}.',
    fromLabel: 'From',
    toLabel: 'To',
    androidChannelNote:
      'Each category has its own Android notification channel, so you can also tune sound and priority per category in system settings.',
  },
  personal: {
    notSet: 'Not set',
    personalMode: 'Personal mode',
    roleInCurrentContext: 'Role in current context',
    roleUnavailable: 'Role unavailable',
    languageNotSet: 'Not set (workspace default, then device)',
    timezoneNotSet: 'Not set (workspace default, then device)',
    languageHelper:
      'Leave empty to use the workspace default, then your device language if it is one of the supported languages, then English (US).',
    timezoneHelper:
      'Leave empty to use your workspace default timezone, then your device timezone, then UTC.',
    effectiveLanguage: 'Effective language',
    effectiveTimezone: 'Effective timezone',
    localeMoved: 'Language and timezone are in Preferences',
    localeMovedA11y: 'Open Preferences to change language and timezone',
    sectionTitle: 'Personal details',
    sectionSubtitle: 'Name, contact, and bio.',
    photoTitle: 'Profile photo',
    photoSubtitle: 'Your photo appears in the shell, team views, and activity. This is separate from workspace or report logos.',
    edit: 'Edit',
    fullName: 'Full name',
    displayName: 'Display name',
    phone: 'Phone',
    language: 'Language',
    timezone: 'Timezone',
    email: 'Email',
    bio: 'Bio',
    shortBio: 'Short bio',
    editTitle: 'Edit profile',
    fieldDisplayName: 'Display name',
    placeholderDisplayName: 'How you want your name to appear',
    fieldFirstName: 'First name',
    placeholderFirstName: 'First name',
    fieldLastName: 'Last name',
    placeholderLastName: 'Last name',
    fieldJobTitle: 'Job title',
    placeholderJobTitle: 'Job title',
    langA11y: 'Language',
    tzA11y: 'Timezone',
    bioPlaceholder: 'A short description for your profile',
    emailReadOnlyHint: "To change your email, go to Account → Security.",
    cancel: 'Cancel',
    saveProfile: 'Save profile',
    pickerLanguageTitle: 'Language',
    pickerTimezoneTitle: 'Timezone',
    searchLanguage: 'Search language or code',
    searchTimezone: 'Search timezone',
    errorInvalidPhone: 'Enter a valid phone number.',
    saveSuccessTitle: 'Saved',
    saveSuccessBody: 'Your profile has been updated.',
    saveErrorGeneric: 'Could not save your profile.',
    phoneNumberLabel: 'Phone number',
    countryCallingCodeA11y: 'Country calling code',
    countryCallingCodeHint: 'Opens list of countries',
    nationalNumberPlaceholder: 'National number',
    nationalNumberA11y: 'Phone national number',
    countryRegionTitle: 'Country or region',
    searchCountry: 'Search country or code',
  },
  overview: {
    tileIndustryPacksSubtitle: "Ready-made asset types, checklists and plans",
    tileIndustryPacksTitle: "Industry packs",
    tileAssetTypesTitle: "Asset types",
    tileAssetTypesSubtitle: "Your own kinds of asset and place, and the details each asks for",
    changePhoto: 'Change photo',
    uploadPhoto: 'Upload photo',
    remove: 'Remove',
    noJobTitle: 'No job title yet',
    profileCompleteness: 'Profile completeness',
    memberSince: 'Member since',
    recentMember: 'Recently joined',
    moreSection: 'More',
    moreSubtitle: 'Workspace, your preferences, and session.',
    personalSection: 'Personal',
    workspaceSection: 'Workspace',
    sessionSection: 'Session',
    workspace: 'Workspace',
    workspaceSubtitle: 'Context, defaults, and report branding.',
    preferences: 'Preferences',
    help: 'Help',
    securitySection: 'Security & privacy',
    privacyData: 'Privacy & data',
    consentHistory: 'Consent history',
    exportData: 'Export my data',
    deleteAccount: 'Delete account',
    signOut: 'Sign out',
    signOutTitle: 'Sign out',
    signOutConfirm: 'Are you sure you want to sign out?',
    signOutCancel: 'Cancel',
    signOutAction: 'Sign out',
    signOutPendingNote:
      'Your pending work stays on this device when you sign out. You can review synchronization and resolve any errors before leaving.',
    reviewPendingWrites: "Review changes waiting to sync",
    signOutErrorTitle: 'Could not finish signing out',
    signOutErrorBody: 'Your saved changes have been retained. Please try again.',
    workspaceTypeMember: 'Member',
    workspaceTypeContractor: 'Contractor',
    workspaceTypeRecipient: 'Recipient',
    teamsCount_one: '{{count}} team',
    teamsCount_other: '{{count}} teams',
    kpiUsersLabel: 'Users',
    kpiUsersHint: 'In this workspace',
    kpiAssetsLabel: 'Assets',
    kpiAssetsHint: 'Workspace total',
    kpiInspectionsLabel: 'Inspections',
    kpiInspectionsHint: 'All records',
    kpiJobsLabel: 'Jobs',
    kpiJobsOpenHint_one: '{{count}} open',
    kpiJobsOpenHint_other: '{{count}} open',
    kpiCreditsLabel: 'AI credits',
    kpiCreditsHint: 'Available balance',
    creditsUnlimited: 'Unlimited',
    groupSettingsA11y_one: '{{title}}, {{count}} setting. {{action}}.',
    groupSettingsA11y_other: '{{title}}, {{count}} settings. {{action}}.',
    groupExpandAction: 'Expand',
    groupCollapseAction: 'Collapse',
    groupYouTitle: 'You',
    groupPlanTitle: "Plan & tools",
    tileOrganizationTitle: 'Organization',
    tileOrganizationSubtitleAdmin: 'Workspace logo, company profile, and legal details',
    tileOrganizationSubtitleMember: 'View the workspace logo, company profile, and legal details',
    tileTeamsTitle: 'Teams & invites',
    tileTeamsSubtitleAdmin: 'Workspace members, roles and invitations',
    tileTeamsSubtitleMember: 'View workspace members, roles and your invitations',
    tileDefaultsTitle: 'Regional defaults',
    tileDefaultsSubtitle: 'Currency, language, timezone, and share methods',
    tileBrandingTitle: 'Report branding',
    tileBrandingSubtitleAdmin: 'PDF/share logo and export details (separate from workspace)',
    tileBrandingSubtitleMember: 'View the PDF/share logo and export details',
    tileIntegrationsTitle: 'Integrations',
    tileIntegrationsSubtitle: "Connect other systems and bring data in",
    tileTemplatesTitle: "Checklists",
    tileTemplatesSubtitle: 'Inspection & job blueprints',
    tileAiTitle: 'AI',
    tileAiSubtitle: 'Usage, automations, audit',
    tileAssistantActivityTitle: 'Assistant activity',
    tileAssistantActivitySubtitle: 'Changes made through the assistant',
    tileYourPlanTitle: '{{plan}} plan',
    tileYourPlanTitleLimit_one: '{{plan}} plan · {{owned}} of {{count}} asset',
    tileYourPlanTitleLimit_other: '{{plan}} plan · {{owned}} of {{count}} assets',
    tileYourPlanTitleUnknown: 'Your plan',
    tileYourPlanSubtitle: 'See the plans and what each includes',
    tileBillingTitle: 'Billing',
    tileBillingSubtitleAdmin: 'Plan, credits, invoices',
    tileBillingSubtitleMember: 'View plan and credit usage',
    tileGuideTitle: 'User guide',
    tileGuideSubtitle: 'Step-by-step guides for every part of AssetWRX',
    tileProfileTitle: 'Profile',
    tileProfileSubtitle: 'Name, photo, contact',
    tilePreferencesSubtitle: "Language, look and phone alerts",
    tileSecurityTitle: 'Security',
    tileSecuritySubtitle: "Email, password and two-factor",
    tilePrivacyTitle: 'Privacy',
    tilePrivacySubtitle: "Your data, our emails, delete account",
    tileFeaturesTitle: "Features",
    tileFeaturesSubtitle: "What is on, off, or locked in this workspace",
    tileDiagnosticsTitle: "Diagnostics",
    tileDiagnosticsSubtitle: "Build, connection and sync state for support",
    tileAboutTitle: "About",
    tileAboutSubtitle: "Version and build",
    groupAppTitle: "App",
    tileHelpTitle: 'Help',
    groupAdvancedTitle: "Advanced",
    tileNotificationsTitle: "Notifications",
    tileNotificationsSubtitle: "Emails and phone alerts you receive",
    tileBrandingDefaultsTitle: "Branding and defaults",
    tileBrandingDefaultsSubtitle: "Organization, report branding, regional defaults",
    tileApiExplorerTitle: "API explorer",
    tileApiExplorerSubtitle: "Try the public API with your keys",
    tileHandbookTitle: "Integration handbook",
    tileHandbookSubtitle: "Guides to connect and automate AssetWRX",
    tileHelpSubtitle: 'How AssetWRX works, and where to get support',
  },
  tabs: {
    summary: 'Summary',
    personal: 'Personal',
    profilePagesA11y: 'Personal settings pages',
  },
  accountScreen: {
    title: 'Account',
    subtitleOverview: 'Manage your profile, workspace, billing, and integrations.',
    subtitleOverviewPersonalOnly: 'Your profile, preferences, security and privacy.',
    subtitlePersonal: 'Your name, contact details, and photo',
    subtitleWorkspace: 'Workspace profile, team, integrations, and billing',
  },
  accountScope: {
    areasA11y: 'Account settings areas',
    userDetails: 'Personal',
    userDetailsHint: 'Your profile, preferences, and help',
    userDetailsTabA11y: 'Personal settings',
    workspace: 'Workspace',
    workspaceHint: 'Organization, team, and administration',
    workspaceTabA11y: 'Workspace settings',
  },
  preferences: {
    title: 'Preferences',
    subtitle: 'Settings that apply only to you.',
    localeTitle: 'Language & region',
    localeHint:
      'How dates, times, and text are shown to you. These follow your account, so they apply on every device you sign in on.',
    localeLanguage: 'Language',
    localeTimezone: 'Timezone',
    localeLanguageA11y: 'Change language',
    localeTimezoneA11y: 'Change timezone',
    localeEffectiveLanguage: 'Currently using {{value}}',
    localeEffectiveTimezone: 'Currently using {{value}}',
    localeSaveError: 'Could not save your language and timezone.',
    notificationsTitle: 'Notifications',
    pushNotifications: 'Push notifications',
    pushNotificationsHint: 'Enable alerts and updates for inspection reviews, jobs, and reminders.',
    haptics: 'Haptic feedback',
    hapticsHint: 'A light tap in your hand when you complete, save or select something.',
    appearanceTitle: 'Appearance',
    appearanceHint:
      'Chooses whether screens use light or dark surfaces on this device. Match system follows your phone or desktop setting.',
    appearanceLight: 'Light',
    appearanceDark: 'Dark',
    appearanceSystem: 'Match system',
    densityComfy: 'Comfy',
    densityComfyHint: 'More breathing room',
    densityCompact: 'Compact',
    densityCompactHint: "Balanced",
    densityDense: 'Dense',
    densityDenseHint: 'Most on screen',
    textSizeDefault: 'Default',
    textSizeLarge: 'Large',
    textSizeExtraLarge: 'Extra large',
    privacyTitle: 'Privacy & data',
    privacyHint: 'Manage your personal data and account.',
    privacyOpenA11y: 'Open Privacy & data',
    deleteAccount: 'Delete account',
    deleteAccountHint: 'Permanently delete your account and personal data.',
    textSizeTitle: 'Text size',
    textSizeHint:
      "Make text throughout the app easier to read. This applies everywhere and works alongside your device's own text-size setting.",
    textSizeA11y: 'Text size {{label}}',
    densityTitle: 'Display density',
    densityHint: 'How much information is packed onto each screen. Applies everywhere.',
    densityA11y: 'Display density {{label}}',
    privacyDataTitle: 'Privacy & data',
    privacyDataHint: 'Privacy Policy, Terms, Export, Communication preferences, Consent history, Delete account',
  },
  deleteAccount: {
    ownerHandOver: "Hand over ownership",
    ownerImpact: "You own this workspace and others work in it. They lose access to it when you delete your account. To keep it for them, hand ownership to someone else first.",
    contactSupport: "Contact AssetWRX support",
    warningTitle: 'This permanently deletes your account',
    warningBody:
      'Deleting your account is irreversible. Once the recovery window passes, your data cannot be restored.',
    consequencesTitle: 'What gets deleted',
    consInspections: 'All inspections you created and their reports',
    consAssets: 'Your assets and their history',
    consMedia: 'Photos, documents, and other media you uploaded',
    consTeams: 'Your team memberships and access to shared workspaces',
    consBilling: 'Active subscriptions are cancelled and billing stops',
    recoveryBody: "Workspaces you own are kept for {{days}} days, then removed for good. To ask about getting them back in that time, contact AssetWRX support. Your personal details are removed straight away.",
    offlineBanner: "You're offline. Reconnect to the internet to delete your account.",
    confirmLabel: 'Type {{token}} to confirm',
    confirmHelper: 'Enter {{token}} exactly as shown to enable the delete button.',
    passwordLabel: 'Confirm your password',
    passwordHelper: "Needed if you sign in with a password. Leave it empty if you sign in with Google, Apple or Microsoft.",
    passwordPlaceholder: 'Your password',
    reauthError: "Please re-enter your password to confirm it's really you.",
    genericError: 'Account deletion could not be completed. Please try again.',
    deleteButton: 'Delete my account',
    deletingStatus: 'Deleting your account…',
    modalTitle: 'Delete account permanently?',
    modalBody:
      'This cannot be undone. Your personal data is anonymized now and solely-owned workspaces are removed after the recovery window.',
    modalConfirm: 'Delete forever',
    modalCancel: 'Keep my account',
    successTitle: 'Your account is being deleted',
    successBody: "You've been signed out. We've started deleting your data and cancelled any active subscriptions. Changed your mind? Contact AssetWRX support within 30 days.",
    complianceNote:
      'You can request account deletion at any time directly in the app, as required by the App Store and Google Play.',
    cancel: 'Cancel',
  },
  featureRequests: {
    editFirst: "Edit first",
    helpRowTitle: "Request a feature",
    helpRowSubtitle: "Tell us what would make AssetWRX better for you.",
    myRequestsTitle: "Your requests",
    myRequestsSubtitle: "What you asked for, and where it is.",
    myRequestsRowSubtitle_one: "{{count}} request sent",
    myRequestsRowSubtitle_other: "{{count}} requests sent",
    myRequestsEmpty: "You have not requested a feature yet.",
    ideasTitle: "Ideas we're considering",
    ideasSubtitle: "Vote for the ones you want. We'll tell you when they move.",
    ideasRowSubtitle: "Vote for what you want next.",
    ideasEmpty: "No ideas are up for votes right now.",
    ideasFooter: "Ideas here are written by the AssetWRX team. Your own requests stay private.",
    loadFailed: "This did not load. Check your connection and pull down to try again.",
    sentOn: "Sent {{date}} · {{category}}",
    mergedNote: "Someone asked for this too, so we joined your request to theirs. You'll hear when it moves.",
    replyFromTeam: "Reply from the AssetWRX team",
    seeWhatsNew: "See what's new: {{title}}",
    onIdeasBoard: "See it on the ideas board",
    vote: "I want this",
    voted: "Voted",
    voteA11y: "Vote for {{title}}",
    unvoteA11y: "Take back your vote for {{title}}",
    voteCount_one: "{{count}} vote",
    voteCount_other: "{{count}} votes",
    voteFailedTitle: "Your vote did not save",
    voteFailedBody: "Check your connection and try again.",
    status: {
      sent: "Sent",
      under_review: "Under review",
      planned: "Planned",
      in_progress: "Being built",
      shipped: "In the app now",
      not_planned: "Not planned for now",
    },
    ideaStatus: {
      considering: "Being considered",
      planned: "Planned",
      in_progress: "Being built",
      shipped: "In the app now",
    },
    category: {
      assets: "Assets and places",
      inspections: "Inspections and checklists",
      work: "Jobs and work",
      schedules: "Schedules",
      reports: "Reports",
      team: "Team and contractors",
      integrations: "Integrations and API",
      other: "Something else",
    },
    importance: {
      nice_to_have: "Nice to have",
      saves_time: "Would save me time",
      blocking: "I can't do my work without it",
    },
    errors: {
      daily_limit: "You've sent 5 requests today. Please send the rest tomorrow.",
      workspace_limit: "Your workspace has sent 20 requests today. Please try again tomorrow.",
      blocked: "Feature requests are turned off for your account. Contact support if you think this is wrong.",
      incomplete: "Add a short title and say what you need.",
      screenshot: "A screenshot did not upload. Remove it or try again.",
      failed: "Your request did not send. Check your connection and try again.",
    },
    form: {
      title: "Request a feature",
      subtitle: "Tell us what you need. The AssetWRX team reads every request.",
      titleLabel: "What would you like?",
      titlePlaceholder: "For example: Print a QR label for each room",
      titleRequired: "Add a short title.",
      descriptionLabel: "How would it help you?",
      descriptionPlaceholder: "What are you trying to do, and what gets in the way today?",
      descriptionRequired: "Say a little about what you need.",
      categoryLabel: "Which part of the app?",
      categoryRequired: "Pick the part of the app it's about.",
      importanceLabel: "How much does it matter?",
      screenshotsLabel: "Screenshots (optional)",
      screenshotsHint: "Up to 3. Please leave out personal or customer details.",
      addScreenshot: "Add a screenshot",
      addScreenshotShort: "Add",
      removeScreenshot: "Remove screenshot",
      photoPermissionTitle: "Allow access to photos",
      photoPermissionBody: "To add a screenshot, allow AssetWRX to see your photos in your phone's Settings.",
      similarTitle: "You asked for something like this before",
      similarHint: "If it's the same, you can follow it in Your requests instead of sending it again.",
      detailsTitle: "Sent with your request",
      detailsHint: "These help us understand where you were. Nothing else from your workspace is sent.",
      detailApp: "App version",
      detailUpdate: "Update",
      detailDevice: "Device",
      detailLanguage: "Language",
      detailScreen: "Screen",
      detailWorkspace: "Workspace",
      detailWorkspaceValue: "Your workspace's name and plan",
      send: "Send request",
      failedTitle: "Not sent",
      sentTitle: "Thanks, we've got it",
      sentBody: "We read every request. You'll get a notification when its status changes or we reply.",
      seeYourRequests: "See your requests",
      done: "Done",
    },
  },
} as const;
